// One app's Theme page: what this app wears, and the theme itself, edited in
// place.
//
// PORTED FROM THE OCTOBER HAND-OFF'S console-themes.jsx (SurfaceTheme, with
// the ThemePicker and ThemeEditor it is built from). Registration › Theme and
// Kiosk › Theme are this one component with different words, which is the
// hand-off's own arrangement: the differences between the apps are copy, not
// controls, so they are passed in rather than forked.
//
// IT REPLACES REGISTRATION'S RAW-TOKEN EDITOR, AND THAT IS THE POINT RATHER
// THAN A REFACTOR. That editor typed `--xp-*` values into a draft that lived
// in this tab and went into the bundle at publish, so one stand's colours were
// one tab's memory, a second app could not share them, and leaving the page
// lost them. Here the app is pointed at a library theme (a row, #199), and the
// theme's colours are edited in the library, measured by the same auditPalette
// the service refuses a save with. #201 has publish write the worn theme into
// the bundle server-side, inside the signing transaction, so the console no
// longer sends colours at all; until #201 is merged a publish from this
// console carries Nova's own colours whatever is chosen here, which is the
// order the two changes are meant to land in.
//
// THE PROTOTYPE'S "WHICH DESIGN SYSTEM" CHOOSER IS NOT HERE, AND IS NOT TO BE
// (Gabe, 12 Oct, decision C8: drop it). An app on its own system wears what it
// ships with; there is nothing in the service to choose between, and a control
// that chose would save nowhere. check.mjs holds the page to that.
//
// EDITING HERE EDITS THE THEME, NOT THIS APP. Every app at every show that
// wears it reads the save, and the lede and the receipt say so. A theme
// nobody here should change is copied first, from the library.

import { useEffect, useState, type ReactNode } from "react";
import { NoticeBox, PageHead, Setting, StickyBar } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import { Confirm, Loading } from "@nova/desk-ui/states";
import { themeSaveProblems, type ThemeRole, type ThemeSurfaceId } from "../../../ui/src/themeLibrary.js";
import type { ApiOptions } from "../api/client.js";
import {
  createLibraryTheme,
  listLibrary,
  readShowThemes,
  sameId,
  saveShowThemes,
  updateLibraryTheme,
  type LibraryRecord,
  type ShowThemes,
  type ThemeDraft,
} from "../api/themes.js";
import type { SessionToken } from "../session/store.js";
import { ContrastTable, failingPairs, ModeSwitch, PaletteEditor, ThemePreviews, TypeAndCorners, type ThemeMode } from "../themes/parts.js";
import {
  blankTheme,
  draftOf,
  FOLLOW,
  followedThemeId,
  nameOf,
  optionValue,
  OWN_SYSTEM,
  pointable,
  pointerOf,
  withPointer,
} from "../themes/model.js";
import { useConsoleToast } from "./Chrome.js";
import { useShow } from "./Show.js";
import { putTheme, ThemeRefused, type ThemeRefusal } from "./Themes.js";

export type SurfaceThemeProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly surface: ThemeSurfaceId;
  /** The app's name in a sentence: "Registration", "Kiosk". */
  readonly label: string;
  /** Drawn as the page head where this is a page of its own; absent where a page around it has one. */
  readonly head?: { readonly title: string; readonly lede: string } | undefined;
  /** Open a theme on the show's Themes page. */
  readonly onOpenTheme: (themeId: string) => void;
  readonly onSessionEnded: (detail: string) => void;
  /** What only this app has, drawn under the editor: Registration's logos. */
  readonly children?: ReactNode;
};

type Read =
  | { readonly status: "reading" }
  | { readonly status: "failed"; readonly refusal: ThemeRefusal }
  | { readonly status: "ready"; readonly show: ShowThemes; readonly themes: readonly LibraryRecord[] };

export function SurfaceTheme({
  api,
  token,
  activationId,
  surface,
  label,
  head,
  onOpenTheme,
  onSessionEnded,
  children,
}: SurfaceThemeProps): ReactNode {
  const say = useConsoleToast();
  const frame = useShow();
  const settled = frame.status === "ready" || frame.status === "failed";
  const [read, setRead] = useState<Read>({ status: "reading" });
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ThemeRefusal | null>(null);
  // Whether the editor below has unsaved changes, and what is waiting on the
  // answer to "discard them?" when something would move it off its theme.
  const [editorDirty, setEditorDirty] = useState(false);
  const [pending, setPending] = useState<{ readonly kind: "point"; readonly value: string } | { readonly kind: "fresh" } | null>(null);

  // After the frame's read of the directory, as the library page waits.
  useEffect(() => {
    if (!settled) return undefined;
    let cancelled = false;
    // A different show, or a fresh read, starts from nothing: the last show's
    // pointers are never drawn here, nor written onto this one (Bugbot on #203).
    setRead({ status: "reading" });
    void (async () => {
      const show = await readShowThemes(api, token, activationId);
      if (cancelled) return;
      if (!show.ok) {
        if (show.kind === "session_ended") onSessionEnded(show.detail);
        setRead({ status: "failed", refusal: show });
        return;
      }
      const themes = await listLibrary(api, token, show.value.clientId);
      if (cancelled) return;
      if (!themes.ok) {
        if (themes.kind === "session_ended") onSessionEnded(themes.detail);
        setRead({ status: "failed", refusal: themes });
        return;
      }
      setRead({ status: "ready", show: show.value, themes: themes.value });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, settled]);

  const pageHead = head === undefined ? null : <PageHead title={head.title} lede={head.lede} uuid={activationId} explain="info" />;

  if (read.status !== "ready") {
    return (
      <div className="stack">
        {pageHead}
        {read.status === "reading" ? <Loading rows={3} cols={2} label="Loading the theme…" /> : null}
        {read.status === "failed" ? <ThemeRefused refusal={read.refusal} /> : null}
        {children}
      </div>
    );
  }

  const { show, themes } = read;
  const pointer = pointerOf(show, surface);
  const wornId = show.surfaces.find((s) => s.surface === surface)?.themeId ?? null;
  const worn = wornId === null ? null : (themes.find((t) => sameId(t.themeId, wornId)) ?? null);
  const eventId = show.showThemeId;
  const followed = nameOf(themes, followedThemeId(show));

  const point = async (
    next: { readonly showThemeId: string | null; readonly surfaceThemes: ShowThemes["surfaceThemes"] },
    receipt: string,
  ): Promise<boolean> => {
    setBusy(true);
    setRefusal(null);
    const saved = await saveShowThemes(api, token, { activationId, ...next });
    setBusy(false);
    if (!saved.ok) {
      if (saved.kind === "session_ended") onSessionEnded(saved.detail);
      setRefusal(saved);
      return false;
    }
    setRead((now) => (now.status === "ready" ? { ...now, show: saved.value } : now));
    say(receipt);
    return true;
  };

  /** A new theme for this app, from the house palette, worn by this app at once. */
  const fresh = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    const made = await createLibraryTheme(api, token, { ...blankTheme(`${label} theme`), clientId: show.clientId });
    if (!made.ok) {
      setBusy(false);
      if (made.kind === "session_ended") onSessionEnded(made.detail);
      setRefusal(made);
      return;
    }
    const themesNow = putTheme(themes, made.value);
    setRead((now) => (now.status === "ready" ? { ...now, themes: putTheme(now.themes, made.value) } : now));
    // Still busy: saveShowThemes replaces every pointer at once, so a select
    // changed or New theme pressed again during this write could overwrite
    // the pointer to the theme just made (Bugbot on #203).
    const saved = await saveShowThemes(api, token, {
      activationId,
      showThemeId: show.showThemeId,
      surfaceThemes: withPointer(show, surface, made.value.themeId),
    });
    setBusy(false);
    if (!saved.ok) {
      if (saved.kind === "session_ended") onSessionEnded(saved.detail);
      setRefusal(saved);
      return;
    }
    setRead((now) => ({ status: "ready", show: saved.value, themes: now.status === "ready" ? putTheme(now.themes, made.value) : themesNow }));
    say(`A new theme for ${label}, started from the house palette.`);
  };

  const pick = (value: string): Promise<boolean> =>
    point(
      { showThemeId: show.showThemeId, surfaceThemes: withPointer(show, surface, value) },
      value === FOLLOW
        ? `${label} follows the show.`
        : value === OWN_SYSTEM
          ? `${label} is on its own design system.`
          : `${label} now wears ${nameOf(themes, value) ?? "that theme"}.`,
    );

  return (
    <div className="stack">
      {pageHead}
      {refusal === null ? null : <ThemeRefused refusal={refusal} />}
      {pending === null ? null : (
        <Confirm
          title={`Discard your changes to ${worn?.name ?? "this theme"}?`}
          verb="Discard"
          danger
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const next = pending;
            setPending(null);
            setEditorDirty(false);
            void (next.kind === "point" ? pick(next.value) : fresh());
          }}
        >
          <p>The colours, type and corners edited below have not been saved, and {pending.kind === "point" ? "pointing this app elsewhere" : "a new theme"} puts the editor on another theme.</p>
        </Confirm>
      )}

      <section>
        <SecLabel>Theme</SecLabel>
        <div className="panel panel-stack">
          <Setting
            label="This app wears"
            info={eventId === null ? "No event theme is set." : `The event theme is ${nameOf(themes, eventId) ?? "set"}.`}
          >
            <select
              className="input"
              name={`surface-theme-${surface}`}
              value={optionValue(themes, pointer)}
              disabled={busy}
              onChange={(event) => {
                const value = event.target.value;
                // Pointing elsewhere puts the editor below on another theme, so
                // unsaved edits there are asked about first (Bugbot on #203).
                if (editorDirty) {
                  setPending({ kind: "point", value });
                  return;
                }
                void pick(value);
              }}
            >
              <option value={FOLLOW}>
                {followed === null ? "Follows the show · its own system" : `Follows the show · ${followed}`}
              </option>
              <option value={OWN_SYSTEM}>Its own design system</option>
              {pointable(themes).map((t) => (
                <option key={t.themeId} value={t.themeId}>
                  {t.name}
                  {sameId(t.themeId, eventId) ? " · event theme" : ""}
                </option>
              ))}
            </select>
          </Setting>
          <div className="acts acts-flush">
            {worn !== null ? (
              <button type="button" className="btn btn-quiet" onClick={() => onOpenTheme(worn.themeId)}>
                Edit {worn.name} in Themes
              </button>
            ) : null}
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => (editorDirty ? setPending({ kind: "fresh" }) : void fresh())}>
              New theme
            </button>
            {worn !== null && !sameId(worn.themeId, eventId) ? (
              <button
                type="button"
                className="btn btn-quiet"
                disabled={busy}
                onClick={() =>
                  void point(
                    { showThemeId: worn.themeId, surfaceThemes: show.surfaceThemes },
                    `${worn.name} is now the event theme. Every app that follows the event wears it.`,
                  )
                }
              >
                Make it the event theme
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {worn === null ? (
        <>
          <section>
            <SecLabel>Edit</SecLabel>
            <div className="panel panel-stack">
              <p className="theme-flush">{label} is on its own design system, which is not editable.</p>
              <div className="acts acts-flush">
                <button type="button" className="btn btn-primary" disabled={busy} onClick={() => (editorDirty ? setPending({ kind: "fresh" }) : void fresh())}>
                  Start a theme for {label}
                </button>
              </div>
            </div>
          </section>
          {children}
        </>
      ) : (
        <InlineEditor
          key={worn.themeId}
          api={api}
          token={token}
          theme={worn}
          label={label}
          onSaved={(stored) => setRead((now) => (now.status === "ready" ? { ...now, themes: putTheme(now.themes, stored) } : now))}
          onSessionEnded={onSessionEnded}
          onDirty={setEditorDirty}
        >
          {children}
        </InlineEditor>
      )}
    </div>
  );
}

/**
 * The hand-off's ThemeEditor: the roles with a chip and a hex, measured
 * readability that gates Save, the client's mark, type and corners, and both
 * previews. It edits the theme the app wears.
 */
function InlineEditor({
  api,
  token,
  theme,
  label,
  onSaved,
  onSessionEnded,
  onDirty,
  children,
}: {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly theme: LibraryRecord;
  readonly label: string;
  readonly onSaved: (stored: LibraryRecord) => void;
  /** Told whenever the editor gains or loses unsaved changes, so the page can ask before moving away from them. */
  readonly onDirty?: (dirty: boolean) => void;
  readonly onSessionEnded: (detail: string) => void;
  readonly children?: ReactNode;
}): ReactNode {
  const say = useConsoleToast();
  const [saved, setSaved] = useState<ThemeDraft>(() => draftOf(theme));
  const [draft, setDraft] = useState<ThemeDraft>(() => draftOf(theme));
  const [mode, setMode] = useState<ThemeMode>("light");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ThemeRefusal | null>(null);
  const locked = theme.locked;
  const dirtyNow = JSON.stringify(draft) !== JSON.stringify(saved);
  useEffect(() => {
    onDirty?.(dirtyNow);
  }, [dirtyNow, onDirty]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const set = (k: ThemeRole, v: string): void => {
    if (!locked) setDraft({ ...draft, [mode]: { ...draft[mode], [k]: v } });
  };

  const save = async (): Promise<void> => {
    setRefusal(null);
    // The hand-off's sentence for the mode on screen, then the other mode's,
    // then anything else themeSaveProblems finds. Refused here, never sent.
    const bad = failingPairs(draft[mode]);
    const first = bad[0];
    if (first !== undefined) {
      setRefusal({ kind: "local", detail: `Refused: ${first.label.toLowerCase()} is below ${first.floor}:1.` });
      return;
    }
    const other = failingPairs(draft[mode === "light" ? "dark" : "light"]);
    if (other.length > 0) {
      setRefusal({
        kind: "local",
        detail: `Refused: ${other.length} pair${other.length === 1 ? "" : "s"} in the other mode ${
          other.length === 1 ? "is" : "are"
        } below the floor. Both modes ship.`,
      });
      return;
    }
    const problems = themeSaveProblems(draft);
    if (problems.length > 0) {
      setRefusal({ kind: "local", detail: `Refused: ${problems.join("; ")}.` });
      return;
    }
    setBusy(true);
    const stored = await updateLibraryTheme(api, token, { ...draft, themeId: theme.themeId });
    setBusy(false);
    if (!stored.ok) {
      if (stored.kind === "session_ended") onSessionEnded(stored.detail);
      setRefusal(stored);
      return;
    }
    const next = draftOf(stored.value);
    setSaved(next);
    setDraft(next);
    onSaved(stored.value);
    say(`Saved. Every app wearing ${next.name} picks it up.`);
  };

  return (
    <>
      {refusal === null ? null : <ThemeRefused refusal={refusal} />}
      <section>
        <SecLabel info="Edited here, saved to the theme: every app at every show that wears it reads the save on its next load, and a stand already published keeps its colours until it is published again.">
          {`Colour · ${theme.name}`}
        </SecLabel>
        <div className="filters theme-mode">
          <ModeSwitch mode={mode} onMode={setMode} />
        </div>
        <PaletteEditor p={draft[mode]} onSet={set} locked={locked} mode={mode} />
      </section>

      <section>
        <SecLabel info="Measured on the palette, so the answer is the same whichever surface wears it. Text needs 4.5:1, the action needs 3:1 to read as a shape, and a hairline is not text.">
          Whether it can be read
        </SecLabel>
        <div className="panel">
          <ContrastTable p={draft[mode]} />
        </div>
      </section>

      {children}

      <section>
        <SecLabel>Type and corners</SecLabel>
        <TypeAndCorners t={draft} locked={locked} onChange={(next) => setDraft({ ...draft, ...next })} />
      </section>

      <section>
        <SecLabel info="Both are drawn because a visitor's own device decides which one they get. A theme that only works in one of them is not finished.">
          What it looks like
        </SecLabel>
        <ThemePreviews t={draft} />
      </section>

      <StickyBar note={dirty ? `Saving changes ${theme.name} for every app that wears it.` : "No unsaved changes."}>
        <button type="button" className="btn btn-quiet" disabled={!dirty || busy} onClick={() => setDraft(saved)}>
          Reset
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!dirty || locked || busy}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </StickyBar>
      {locked ? (
        <NoticeBox kind="amber" title="This is a shipped theme">
          <p>Nova house is read-only. Open it in Themes and Copy to edit, to make a version {label} can wear.</p>
        </NoticeBox>
      ) : null}
    </>
  );
}
